import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CookieStorageService } from '@df/browser/storage/cookie-storage.service';
import { ConfigResource } from '@df/catalog/config/config.resource';
import { IProductDataV2 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { App } from '@df/core/app';
import { OptionLabelPipe } from '@df/ui/common/option-label.pipe';
import { Lib } from '@tomandco/nexus-api-types/nexus/lib';
import { CMS_CONFIG_SIZEBAY } from '../../cms/config/sizebay.cms-config';

type DeviceType = 'DESKTOP' | 'TABLET' | 'MOBILE';

interface DeviceSizeMap {
    [key: number]: DeviceType;
}

export function initializeSizebayService(sizebayService: SizebayService) {
    return (): Promise<void> => {
        return sizebayService.init();
    };
}

@Injectable({
    providedIn: 'root'
})
export class SizebayService {
    private readonly tenantId = '5483';
    private sizebayEnabled = false;
    private readonly deviceSizeMap: DeviceSizeMap = {
        3: 'DESKTOP',
        2: 'DESKTOP',
        1: 'TABLET'
    } as const;
    private readonly defaultDevice: DeviceType = 'MOBILE';
    private optionLabelPipe = new OptionLabelPipe();
    private cookieStorageService = inject(CookieStorageService);
    private http = inject(HttpClient);
    private app = inject(App);
    private productResource = inject(ProductResource);
    private configResource = inject(ConfigResource);

    init() {
        return this.loadScript();
    }

    private async loadScript() {
        const script = document.createElement('script');
        script.id = 'sizebay-vfr-v4';
        script.src = `https://static.sizebay.technology/${this.tenantId}/prescript.js`;
        document.head.appendChild(script);
    }

    async addToBasketCall(productSkus: string[]): Promise<void> {
        const sizebayId = this.cookieStorageService.get('SIZEBAY_SESSION_ID_V4');
        await this.loadSizebayConfig();
        if (!this.sizebayEnabled || !sizebayId) {
            return;
        }
        this.http
            .post(
                'https://vfr-v3-production.sizebay.technology/plugin/new/cart',
                {
                    products: productSkus.map(sku => ({
                        permalink: `${this.app.url.storeUrl}${sku}`
                    })),
                    tenantId: this.tenantId
                },
                {
                    headers: {
                        'x-szb-country': this.app.multisite.current.country,
                        'x-szb-device': this.currentDevice,
                        'x-szb-tenant-id': this.tenantId,
                        referer: this.app.url.storeUrl.replace('https://', '')
                    },
                    params: {
                        sid: sizebayId
                    }
                }
            )
            .subscribe();
    }

    async orderPlacementCall(orderId: string, checkoutItems: Lib.Customer.Order.HistoryOrderItem[]): Promise<void> {
        const sizebayId = this.cookieStorageService.get('SIZEBAY_SESSION_ID_V4');
        await this.loadSizebayConfig();
        if (!this.sizebayEnabled || !sizebayId) {
            return;
        }
        const products = await this.productResource.loadAllIds(
            checkoutItems.map(item => item.product_id),
            2,
            {
                safe: true
            }
        );
        this.http
            .post(
                'https://vfr-v3-production.sizebay.technology/plugin/new/ordered',
                {
                    orderId,
                    items: products.map(product => {
                        const checkoutItem = checkoutItems.find(item => item.product_id === product.id);
                        return {
                            permalink: `${this.app.url.storeUrl}${product?.parent?.data?.sku || product?.data?.sku}`,
                            price: checkoutItem?.price,
                            quantity: (checkoutItem as any)?.qty,
                            size: this.optionLabelPipe.transform((product.data as IProductDataV2)?.size, 'size'),
                            feedProductId: product.parent.data?.sku,
                            sku: product.data?.sku
                        };
                    }),
                    tenantId: this.tenantId,
                    currency: this.app.multisite.current.currency
                },
                {
                    headers: {
                        'x-szb-country': this.app.multisite.current.country,
                        'x-szb-device': this.currentDevice,
                        'x-szb-tenant-id': this.tenantId,
                        referer: this.app.url.storeUrl.replace('https://', '')
                    },
                    params: {
                        sid: sizebayId
                    }
                }
            )
            .subscribe();
    }

    private get currentDevice(): DeviceType {
        return this.deviceSizeMap[this.app.client.sizeId] ?? this.defaultDevice;
    }

    private async loadSizebayConfig(): Promise<void> {
        await this.configResource.loadModelByUrl(CMS_CONFIG_SIZEBAY);
        this.sizebayEnabled = this.configResource.getModelByUrl(CMS_CONFIG_SIZEBAY)?.data?.content?.sizebayEnabled ?? false;
    }
}
